import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Button,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  TuneOutlined,
  BoltOutlined,
  ChecklistRtlOutlined,
  HandshakeOutlined,
  ListAltOutlined,
  AttachMoneyOutlined,
  SettingsOutlined,
  AccountTreeOutlined,
  FiberManualRecord,
  SaveOutlined,
  Close,
  Cancel,
  AccessTime,
  CancelOutlined,
  DrawOutlined,
} from "@mui/icons-material";
import { useModalStyles } from "../../common/useModalStyles";
import FieldFocusPopup from "../../common/FieldFocusPopup";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import {
  getProductConfigTemplate,
  getProductConfigByProduct,
  saveProductConfig,
  ProductConfigTemplate,
  ProductConfigRules,
  SaveProductConfigRuleItem,
} from "../../../service/ProductService";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { tokens as paletteTokens } from "../../../theme";

interface ProductConfigPopupProps {
  open: boolean;
  onClose: () => void;
  productId: number;
  canEdit: boolean;
  isDraftMode?: boolean;
  initialConfigRules?: ProductConfigRules[];
  onDraftSave?: (configRules: ProductConfigRules[]) => void;
  /** Called after a successful API save so the parent can refresh dependent UI (e.g. Product COGs). */
  onSaveSuccess?: (configRules: ProductConfigRules[]) => void;
  productName?: string | null;
  embedded?: boolean;
}

interface SelectedConfigRule extends SaveProductConfigRuleItem { }

const CONFIG_CODE_LABELS: Record<string, string> = {
  PATIENT: "Patient Info",
  IVR: "IVR Verification",
  PRESCRIPTION: "Rx",
  MODE: "Agreement Mode",
  TYPE: "Form Type",
  UPFRONT_PRICING: "Upfront Pricing",
  PURCHASE_AND_BILL: "Purchase & Bill",
  ENABLE: "Enable",
};

const VALUE_LABELS: Record<string, string> = {
  REQUIRED: "Required",
  OPTIONAL: "Optional",
  EXCLUDED: "Not Needed",
  MANDATORY: "Mandatory",
  AUTO: "Auto Accept",
  NONE: "None",
  SKIN_SUB: "Skin Substitute",
  COLLAGEN: "Collagen",
  OPEN_ACCESS: "Open Access",
  true: "Yes",
  false: "No",
  Y: "Yes",
  N: "No",
};

// CS-1845 START - Product config dependency constants
const PURCHASE_AND_BILL_TYPE = "PURCHASE_AND_BILL";
const UPFRONT_PRICING_TYPE = "UPFRONT_PRICING";
const LATE_FEE_TYPE = "LATE_FEE";
// CS-2090 - Auto signature is only offered while Purchase & Bill is enabled, and defaults to on.
const AUTO_SIGNATURE_TYPE = "AUTO_SIGNATURE";
const ENABLE_CODE = "ENABLE";
const YES_VALUE = "Y";
const NO_VALUE = "N";
const UPFRONT_DISCOUNT_METADATA_KEY = "upfrontDiscount";
// CS-1845 END
// CS-1764 - Late fee metadata key
const LATE_FEE_PERCENT_METADATA_KEY = "lateFeePercent";

/** Same reading order as the Product Rules grid: column 1 → 2 → 3 (top to bottom within each column). */
const ACTIVE_CHIP_SECTION_ORDER: Record<string, number> = {
  ORDER_REQUIREMENT: 0,
  ORDER_WORKFLOW: 1,
  AGREEMENT: 2,
  [LATE_FEE_TYPE]: 3,
  [PURCHASE_AND_BILL_TYPE]: 4,
  // CS-2090 - Auto signature inserted after Purchase & Bill; the entries below shifted by one.
  // Previous version, kept for reference:
  // [UPFRONT_PRICING_TYPE]: 5,
  // ORDER_FORM: 6,
  // PRICING: 7,
  [AUTO_SIGNATURE_TYPE]: 5,
  [UPFRONT_PRICING_TYPE]: 6,
  ORDER_FORM: 7,
  PRICING: 8,
};

const ProductConfigPopup: React.FC<ProductConfigPopupProps> = ({
  open,
  onClose,
  productId,
  canEdit,
  isDraftMode = false,
  initialConfigRules = [],
  onDraftSave,
  onSaveSuccess,
  productName: rawProductName,
  embedded = false,
}) => {
  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // App-wide brand palette (primary/secondary/info/success/warning/error) — keeps this
  // popup's section/status colors in sync with the rest of the app instead of one-off hex.
  const colors = paletteTokens(theme.palette.mode);

  const CONFIG_TYPE_META: Record<
    string,
    { label: string; description: string; icon: React.ReactElement; color: string }
  > = {
    ORDER_REQUIREMENT: {
      label: "Order Requirement",
      description: "Define required elements for order processing",
      icon: <ChecklistRtlOutlined sx={{ fontSize: 18 }} />,
      color: colors.info[300],
    },
    AGREEMENT: {
      label: "Agreement",
      description: "Configure agreement and consent handling",
      icon: <HandshakeOutlined sx={{ fontSize: 18 }} />,
      color: colors.secondary[300],
    },
    ORDER_FORM: {
      label: "Order Form",
      description: "Set order form type and layout",
      icon: <ListAltOutlined sx={{ fontSize: 18 }} />,
      color: colors.warning[300],
    },
    PRICING: {
      label: "Pricing",
      description: "Control purchase, billing, and payment settings",
      icon: <AttachMoneyOutlined sx={{ fontSize: 18 }} />,
      color: colors.error[300],
    },
    PURCHASE_AND_BILL: {
      label: "Purchase & Bill",
      description: "Allow practice to purchase and bill separately",
      icon: <AttachMoneyOutlined sx={{ fontSize: 18 }} />,
      color: colors.primary[300],
    },
    // CS-2090 - Owner side auto signature on Purchase & Bill agreements. No card of its own; the
    // toggle renders inside the Purchase & Bill card, so this entry only supplies label/icon/color.
    AUTO_SIGNATURE: {
      label: "Auto Signature",
      description: "Auto sign the V3 side once the practice signs",
      icon: <DrawOutlined sx={{ fontSize: 18 }} />,
      color: colors.secondary[300],
    },
    UPFRONT_PRICING: {
      label: "Upfront Pricing",
      description: "Allow practices to pay upfront at a discounted rate",
      icon: <BoltOutlined sx={{ fontSize: 18 }} />,
      color: colors.primary[300],
    },
    // CS-1764 - Late Fee configuration section
    LATE_FEE: {
      label: "Late Fee",
      description: "Apply a late fee percentage on overdue invoices",
      icon: <AccessTime sx={{ fontSize: 18 }} />,
      color: colors.warning[300],
    },
    ORDER_WORKFLOW: {
      label: "Order Workflow",
      description: "Set order workflow type and layout",
      icon: <AccountTreeOutlined sx={{ fontSize: 18 }} />,
      color: colors.primary[300],
    },
  };

  const getDotColor = (value: string): string => {
    if (["REQUIRED", "MANDATORY", "true", "SKIN_SUB", "Y"].includes(value)) return colors.success[300];
    if (["OPTIONAL"].includes(value)) return colors.warning[300];
    if (["COLLAGEN"].includes(value)) return colors.info[300];
    if (["AUTO", "OPEN_ACCESS"].includes(value)) return colors.secondary[300];
    if (["EXCLUDED", "false", "N"].includes(value)) return colors.error[300];
    return colors.secondary[300];
  };

  const getSelectedStyle = (value: string): { bg: string; text: string; border: string } => {
    if (["REQUIRED", "MANDATORY", "true", "Y", "SKIN_SUB"].includes(value))
      return { bg: `${colors.success[300]}1f`, text: colors.success[300], border: `${colors.success[300]}47` };
    if (["EXCLUDED", "false", "N"].includes(value))
      return { bg: `${colors.error[300]}1a`, text: colors.error[300], border: `${colors.error[300]}3d` };
    if (["OPTIONAL"].includes(value))
      return { bg: `${colors.warning[300]}1a`, text: colors.warning[300], border: `${colors.warning[300]}38` };
    if (["AUTO", "OPEN_ACCESS"].includes(value))
      return { bg: `${colors.secondary[300]}14`, text: colors.secondary[300], border: `${colors.secondary[300]}59` };
    if (["COLLAGEN"].includes(value))
      return { bg: `${colors.info[300]}1a`, text: colors.info[300], border: `${colors.info[300]}3d` };
    return { bg: "rgba(148,163,184,0.08)", text: "#94A3B8", border: "rgba(148,163,184,0.24)" };
  };

  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const [template, setTemplate] = useState<ProductConfigTemplate>({});
  const [selectedRules, setSelectedRules] = useState<Record<string, SelectedConfigRule>>({});
  const [originalRules, setOriginalRules] = useState<Record<string, SelectedConfigRule>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const productName = typeof rawProductName === "string" ? rawProductName.trim() : "";

  const getLabel = useCallback((tableName: string, keyName: string, defaultValue?: string) => {
    const fallback = defaultValue || keyName;
    if (!allCodeTablesValuesMap) return fallback;
    // @ts-ignore
    const tableData = allCodeTablesValuesMap[tableName];
    if (!tableData || !Array.isArray(tableData)) return fallback;
    const entry = tableData.find((item: any) => item.keyName === String(keyName));
    return entry ? entry.displayValue : fallback;
  }, [allCodeTablesValuesMap]);

  // const getConfigTooltip = useCallback((type: string, code: string, value: string): string => {
  //   const codeLabel = type === "PURCHASE_AND_BILL" ? "Purchase & Bill" : getLabel("productConfigLabels", code);
  //   const name = codeLabel.replace(" Info", "").trim();
  //   const v = String(value).toUpperCase();

  //   const tooltips: Record<string, string> = {
  //     REQUIRED: `Choose when ${name} must be filled for compliance or billing, ensuring the order cannot proceed without it.`,
  //     MANDATORY: `Choose when strict regulatory enforcement is needed for ${name}, ensuring it is always required with no bypass.`,
  //     OPTIONAL: `Choose when ${name} is helpful but not critical, allowing users to skip it without blocking the order.`,
  //     EXCLUDED: `Choose when ${name} is not relevant, completely removing it from the workflow and data collection.`,
  //     NONE: `Choose when no logic or enforcement is needed for ${name}, ensuring the system ignores this field entirely.`,
  //     FALSE: `Choose when ${name} is not needed, completely disabling it from the workflow.`,
  //     TRUE: `Choose when ${name} must be enforced, ensuring it is always active and applied.`,
  //     AUTO: `Choose when you want the system to handle ${name} validation automatically, reducing manual effort and applying rules dynamically.`,
  //     SKIN_SUB: `Choose when the product is a CTP, ensuring advanced clinical workflows and specialized billing are applied.`,
  //     COLLAGEN: `Choose when the product is collagen-based, ensuring a standard and simpler fulfillment workflow.`,
  //     OPEN_ACCESS: `Choose when minimal restrictions are needed, enabling faster provider-driven ordering with fewer validations.`,
  //     Y: `Choose when V3 handles ${name}, using V3 templates to create and manage invoices directly.`,
  //     N: `Choose when the manufacturer handles ${name}, using product-level templates to apply invoices independently.`,
  //   };

  //   return tooltips[v] || `${name} is configured as ${getLabel("productConfigLabels", value)}.`;
  // }, [getLabel]);
  const getConfigTooltip = useCallback((type: string, code: string, value: string): string => {
    const codeLabel = type === "PURCHASE_AND_BILL" ? "Purchase & Bill" : getLabel("productConfigLabels", code);
    const name = codeLabel.replace(" Info", "").trim();
    const v = String(value).toUpperCase();

    // Special handling for Upfront Pricing to provide specific tooltips
    if (type === "UPFRONT_PRICING") {
      if (v === "Y" || v === "YES") {
        return "Enable upfront pricing to allow practices to pay discounted rates at time of order";
      } else if (v === "N" || v === "NO") {
        return "Disable upfront pricing - practices will pay standard rates at time of invoice";
      }
      // For other values, fall through to generic handling
    }

    // CS-2090 - Auto Signature tooltip handling
    if (type === AUTO_SIGNATURE_TYPE) {
      if (v === "Y" || v === "YES") {
        return "Enable auto signature - The countersignature will be applied automatically once the practice signs.";
      } else if (v === "N" || v === "NO") {
        return "Disable auto signature - the V3 side of the agreement has to be counter signed manually after the practice signs";
      }
      // For other values, fall through to generic handling
    }

    // CS-1764 - Late Fee tooltip handling
    if (type === "LATE_FEE") {
      if (v === "Y" || v === "YES") {
        return "Enable late fee to automatically apply a percentage charge on overdue invoice balances";
      } else if (v === "N" || v === "NO") {
        return "Disable late fee - no additional charges will be applied on overdue invoices";
      }
      // For other values, fall through to generic handling
    }

    const tooltips: Record<string, string> = {
      REQUIRED: `Choose when ${name} must be filled for compliance or billing, ensuring the order cannot proceed without it.`,
      MANDATORY: `Choose when strict regulatory enforcement is needed for ${name}, ensuring it is always required with no bypass.`,
      OPTIONAL: `Choose when ${name} is helpful but not critical, allowing users to skip it without blocking the order.`,
      EXCLUDED: `Choose when ${name} is not relevant, completely removing it from the workflow and data collection.`,
      NONE: `Choose when no logic or enforcement is needed for ${name}, ensuring the system ignores this field entirely.`,
      FALSE: `Choose when ${name} is not needed, completely disabling it from the workflow.`,
      TRUE: `Choose when ${name} must be enforced, ensuring it is always active and applied.`,
      AUTO: `Choose when you want the system to handle ${name} validation automatically, reducing manual effort and applying rules dynamically.`,
      SKIN_SUB: `Choose when the product is a CTP, ensuring advanced clinical workflows and specialized billing are applied.`,
      COLLAGEN: `Choose when the product is collagen-based, ensuring a standard and simpler fulfillment workflow.`,
      OPEN_ACCESS: `Choose when minimal restrictions are needed, enabling faster provider-driven ordering with fewer validations.`,
      Y: type === "PURCHASE_AND_BILL"
        ? `Choose when V3 handles ${name}, using V3 templates to create and manage invoices directly.`
        : `Choose when ${name} is enabled.`,
      N: type === "PURCHASE_AND_BILL"
        ? `Choose when the manufacturer handles ${name}, using product-level templates to apply invoices independently.`
        : `Choose when ${name} is disabled.`,
    };

    return tooltips[v] || `${name} is configured as ${getLabel("productConfigLabels", value)}.`;
}, [getLabel]);

  const configKey = (type: string, code: string) => `${type}.${code}`;
  const purchaseAndBillEnabled =
    selectedRules[configKey(PURCHASE_AND_BILL_TYPE, ENABLE_CODE)]?.value === YES_VALUE;
  const hasUpfrontPricingConfig = Boolean(template[UPFRONT_PRICING_TYPE]?.[ENABLE_CODE]);
  // CS-2090 - Auto signature follows the same Purchase & Bill dependency as upfront pricing.
  const autoSignatureKey = configKey(AUTO_SIGNATURE_TYPE, ENABLE_CODE);
  // CS-2090 - Allowed values are read from the template so the toggle can be rendered from inside
  // the Purchase & Bill card instead of from its own card.
  const autoSignatureAllowedValues = template[AUTO_SIGNATURE_TYPE]?.[ENABLE_CODE];
  const hasAutoSignatureConfig = Boolean(autoSignatureAllowedValues);
  const autoSignatureValue = selectedRules[autoSignatureKey]?.value || NO_VALUE;
  const isSectionVisible = (configType: string) =>
    ![UPFRONT_PRICING_TYPE, AUTO_SIGNATURE_TYPE].includes(configType) || purchaseAndBillEnabled;
  const visibleTemplateEntries = Object.entries(template).filter(([configType]) => isSectionVisible(configType));

  const mapRulesByKey = (rules: Array<SaveProductConfigRuleItem | ProductConfigRules>) => {
    const nextRules: Record<string, SelectedConfigRule> = {};
    rules.forEach((rule) => {
      nextRules[configKey(rule.configType, rule.configCode)] = {
        configType: rule.configType,
        configCode: rule.configCode,
        value: rule.value,
        metadata: (rule as SaveProductConfigRuleItem).metadata,
      };
    });
    return nextRules;
  };

  const fetchData = useCallback(async () => {
    if (!open && !embedded) return;

    setLoading(true);
    try {
      const [templateRes, configRes] = await Promise.all([
        getProductConfigTemplate(),
        !isDraftMode && productId > 0 ? getProductConfigByProduct(productId) : Promise.resolve({ data: { data: initialConfigRules } }),
      ]);
      const nextTemplate: ProductConfigTemplate = templateRes?.data?.data || {};
      const savedConfigs: ProductConfigRules[] = configRes?.data?.data || [];
      const rules = mapRulesByKey(savedConfigs);
      setTemplate(nextTemplate);
      setSelectedRules(rules);
      setOriginalRules(rules);
    } catch {
      dispatch(setSingleAlertObj({ message: "Failed to load configuration", alertType: "error" } as SingleAlertInfo));
      setTemplate({});
      setSelectedRules({});
      setOriginalRules({});
    } finally {
      setLoading(false);
    }
  }, [dispatch, embedded, initialConfigRules, isDraftMode, open, productId]);

  useEffect(() => { if (open || embedded) fetchData(); }, [open, embedded, fetchData]);

  const handleSelect = (type: string, code: string, value: string) => {
    if (!canEdit) return;
    const key = configKey(type, code);
    setSelectedRules((prev) => {
      const isUpfrontPricingEnable = type === UPFRONT_PRICING_TYPE && code === ENABLE_CODE;
      const metadata = isUpfrontPricingEnable && value === YES_VALUE
        ? {
          ...prev[key]?.metadata,
          [UPFRONT_DISCOUNT_METADATA_KEY]: prev[key]?.metadata?.[UPFRONT_DISCOUNT_METADATA_KEY] ?? 0,
        }
        : prev[key]?.metadata;
      const next = {
        ...prev,
        [key]: {
          configType: type,
          configCode: code,
          value,
          metadata,
        },
      };

      // CS-1845 - Upfront pricing is only configurable when Purchase & Bill is enabled.
      if (type === PURCHASE_AND_BILL_TYPE && code === ENABLE_CODE && value === NO_VALUE) {
        const upfrontKey = configKey(UPFRONT_PRICING_TYPE, ENABLE_CODE);
        next[upfrontKey] = {
          configType: UPFRONT_PRICING_TYPE,
          configCode: ENABLE_CODE,
          value: NO_VALUE,
          metadata: prev[upfrontKey]?.metadata ?? null,
        };
      }

      // CS-2090 - Turning Purchase & Bill on defaults auto signature to enabled.
      if (type === PURCHASE_AND_BILL_TYPE && code === ENABLE_CODE && value === YES_VALUE
        && !prev[autoSignatureKey]?.value) {
        next[autoSignatureKey] = {
          configType: AUTO_SIGNATURE_TYPE,
          configCode: ENABLE_CODE,
          value: YES_VALUE,
          metadata: null,
        };
      }

      return next;
    });
  };

  const handleUpfrontDiscountChange = (value: string) => {
    if (!canEdit) return;
    const numericValue = Number(value);
    const normalizedValue = value === "" || Number.isNaN(numericValue) ? "" : Math.max(0, Math.min(100, numericValue));
    const key = configKey(UPFRONT_PRICING_TYPE, ENABLE_CODE);

    setSelectedRules((prev) => ({
      ...prev,
      [key]: {
        configType: UPFRONT_PRICING_TYPE,
        configCode: ENABLE_CODE,
        value: YES_VALUE,
        metadata: {
          ...prev[key]?.metadata,
          // CS-1845 - Discount percent field belongs to enabled upfront pricing configuration.
          [UPFRONT_DISCOUNT_METADATA_KEY]: normalizedValue,
        },
      },
    }));
  };

  // CS-1764 - Handler for late fee percentage change
  const handleLateFeePercentChange = (value: string) => {
    if (!canEdit) return;
    const numericValue = Number(value);
    const normalizedValue = value === "" || Number.isNaN(numericValue) ? "" : Math.max(0, Math.min(100, numericValue));
    const key = configKey(LATE_FEE_TYPE, ENABLE_CODE);

    setSelectedRules((prev) => ({
      ...prev,
      [key]: {
        configType: LATE_FEE_TYPE,
        configCode: ENABLE_CODE,
        value: YES_VALUE,
        metadata: {
          ...prev[key]?.metadata,
          [LATE_FEE_PERCENT_METADATA_KEY]: normalizedValue,
        },
      },
    }));
  };

  const handleSave = async () => {
    if (saving || loading) return;

    // CS-1764 - Late Fee is optional, exclude from required validation
    const missingSelections = visibleTemplateEntries.flatMap(([configType, configCodes]) =>
      Object.keys(configCodes)
        .filter((configCode) => !(configType === UPFRONT_PRICING_TYPE && configCode === ENABLE_CODE))
        .filter((configCode) => !(configType === LATE_FEE_TYPE && configCode === ENABLE_CODE))
        // CS-2090 - Auto signature always resolves to a value through its default, never "missing".
        .filter((configCode) => !(configType === AUTO_SIGNATURE_TYPE && configCode === ENABLE_CODE))
        .filter((configCode) => !selectedRules[configKey(configType, configCode)]?.value)
        .map((configCode) => getLabel("productConfigLabels", configCode))
    );

    if (missingSelections.length > 0) {
      dispatch(setSingleAlertObj({
        message: `Please select a value for all product config options before saving. Missing: ${missingSelections.join(", ")}`,
        alertType: "error",
      } as SingleAlertInfo));
      return;
    }

    const upfrontRule = selectedRules[configKey(UPFRONT_PRICING_TYPE, ENABLE_CODE)];
    if (upfrontRule?.value === YES_VALUE) {
      const discountValue = Number(upfrontRule.metadata?.[UPFRONT_DISCOUNT_METADATA_KEY]);
      if (Number.isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
        dispatch(setSingleAlertObj({
          message: "Please enter an upfront discount percentage between 0 and 100.",
          alertType: "error",
        } as SingleAlertInfo));
        return;
      }
    }

    // CS-1764 - Validate late fee percentage when enabled
    const lateFeeRule = selectedRules[configKey(LATE_FEE_TYPE, ENABLE_CODE)];
    if (lateFeeRule?.value === YES_VALUE) {
      const lateFeeValue = Number(lateFeeRule.metadata?.[LATE_FEE_PERCENT_METADATA_KEY]);
      if (Number.isNaN(lateFeeValue) || lateFeeValue < 0 || lateFeeValue > 100) {
        dispatch(setSingleAlertObj({
          message: "Please enter a late fee percentage between 0 and 100.",
          alertType: "error",
        } as SingleAlertInfo));
        return;
      }
    }

    const selectedRulesForSave = { ...selectedRules };
    if (hasUpfrontPricingConfig) {
      const upfrontKey = configKey(UPFRONT_PRICING_TYPE, ENABLE_CODE);
      const existingUpfrontRule = selectedRulesForSave[upfrontKey];

      // CS-1845 - Always send upfront pricing; when hidden by Purchase & Bill = N, force it off.
      selectedRulesForSave[upfrontKey] = {
        configType: UPFRONT_PRICING_TYPE,
        configCode: ENABLE_CODE,
        value: purchaseAndBillEnabled ? existingUpfrontRule?.value || NO_VALUE : NO_VALUE,
        metadata: purchaseAndBillEnabled && existingUpfrontRule?.value === YES_VALUE ? existingUpfrontRule?.metadata : null,
      };
    }

    // CS-2090 - Always send auto signature so the rule is never dropped while hidden.
    // Defaults to enabled with Purchase & Bill on, and is forced off when Purchase & Bill is off.
    if (hasAutoSignatureConfig) {
      selectedRulesForSave[autoSignatureKey] = {
        configType: AUTO_SIGNATURE_TYPE,
        configCode: ENABLE_CODE,
        value: purchaseAndBillEnabled ? autoSignatureValue : NO_VALUE,
        metadata: null,
      };
    }

    // CS-1764 - Always send late fee config; default to disabled if not set
    const hasLateFeeConfig = Boolean(template[LATE_FEE_TYPE]?.[ENABLE_CODE]);
    if (hasLateFeeConfig) {
      const lateFeeKey = configKey(LATE_FEE_TYPE, ENABLE_CODE);
      const existingLateFeeRule = selectedRulesForSave[lateFeeKey];
      selectedRulesForSave[lateFeeKey] = {
        configType: LATE_FEE_TYPE,
        configCode: ENABLE_CODE,
        value: existingLateFeeRule?.value || NO_VALUE,
        metadata: existingLateFeeRule?.value === YES_VALUE ? existingLateFeeRule?.metadata : null,
      };
    }

    // CS-2090 - Previous version, kept for reference:
    // const configRules = Object.values(selectedRulesForSave)
    //   .filter((rule) => isSectionVisible(rule.configType) || rule.configType === UPFRONT_PRICING_TYPE)
    const configRules = Object.values(selectedRulesForSave)
      .filter((rule) => isSectionVisible(rule.configType)
        || rule.configType === UPFRONT_PRICING_TYPE
        || rule.configType === AUTO_SIGNATURE_TYPE)
      .map((rule) => ({
        productId,
        configType: rule.configType,
        configCode: rule.configCode,
        value: rule.value,
        metadata: rule.metadata,
      }));

    if (isDraftMode) {
      onDraftSave?.(configRules);
      dispatch(setSingleAlertObj({ title: "Success", message: "Product configuration saved to draft", alertType: "success" } as SingleAlertInfo));
      onClose();
      return;
    }

    // For existing products, skip save if nothing changed
    const isEdit = Object.keys(originalRules).length > 0;
    if (isEdit) {
      const hasChanges = Object.keys(selectedRulesForSave).some((key) => {
        const current = selectedRulesForSave[key]?.value;
        const original = originalRules[key]?.value;
        const currentMetadata = JSON.stringify(selectedRulesForSave[key]?.metadata || {});
        const originalMetadata = JSON.stringify(originalRules[key]?.metadata || {});
        return current !== original || currentMetadata !== originalMetadata;
      }) || Object.keys(originalRules).some((key) => !selectedRulesForSave[key]);

      if (!hasChanges) {
        dispatch(setSingleAlertObj({ message: "No changes detected", alertType: "info" } as SingleAlertInfo));
        return;
      }
    }

    if (productId <= 0) return;

    setSaving(true);
    try {
      const response = await saveProductConfig({
        productId,
        configRules: configRules.map(({ configType, configCode, value, metadata }) => ({
          configType,
          configCode,
          value,
          metadata,
        })),
      });
      if (response?.data?.code === "V3OK") {
        setOriginalRules({ ...selectedRulesForSave });
        onSaveSuccess?.(configRules);
        dispatch(setSingleAlertObj({ title: "Success", message: "Saved successfully", alertType: "success" } as SingleAlertInfo));
        if (!embedded) {
          onClose();
        }
      } else {
        dispatch(setSingleAlertObj({ message: response?.data?.message || "Failed to save", alertType: "error" } as SingleAlertInfo));
      }
    } catch {
      dispatch(setSingleAlertObj({ message: "Failed to save", alertType: "error" } as SingleAlertInfo));
    } finally {
      setSaving(false);
    }
  };

  const activeEntries = useMemo(() => {
    const entries: Array<[string, SelectedConfigRule]> = Object.entries(selectedRules).filter(
      ([, rule]) => Boolean(rule?.value) && isSectionVisible(rule.configType)
    );

    // CS-2090 - Show the defaulted auto signature chip when the product has no rule stored yet.
    if (hasAutoSignatureConfig && purchaseAndBillEnabled && !selectedRules[autoSignatureKey]?.value) {
      entries.push([
        autoSignatureKey,
        { configType: AUTO_SIGNATURE_TYPE, configCode: ENABLE_CODE, value: autoSignatureValue },
      ]);
    }

    entries.sort(([keyA, ruleA], [keyB, ruleB]) => {
      const rank = (t: string) => ACTIVE_CHIP_SECTION_ORDER[t] ?? 100;
      const d = rank(ruleA.configType) - rank(ruleB.configType);
      if (d !== 0) return d;
      const codeA = keyA.includes(".") ? keyA.slice(keyA.indexOf(".") + 1) : "";
      const codeB = keyB.includes(".") ? keyB.slice(keyB.indexOf(".") + 1) : "";
      return codeA.localeCompare(codeB);
    });
    return entries;
    // CS-2090 - Previous dependency list, kept for reference: }, [selectedRules]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRules, hasAutoSignatureConfig, purchaseAndBillEnabled, autoSignatureValue]);

  const buildCardColumns = (): string[][] => {
    // CS-1845 - Keep product config cards aligned with the approved reference layout.
    // CS-2090 - Auto signature has no card of its own; its toggle is rendered inside the
    // Purchase & Bill card, so it is excluded here (also from the unplaced-types fallback below).
    const visibleTypes = visibleTemplateEntries
      .map(([key]) => key)
      .filter((key) => key !== AUTO_SIGNATURE_TYPE);
    const columns = [
      ["ORDER_REQUIREMENT", "ORDER_WORKFLOW"],
      ["AGREEMENT", LATE_FEE_TYPE],
      [PURCHASE_AND_BILL_TYPE, UPFRONT_PRICING_TYPE],
    ].map((column) => column.filter((key) => visibleTypes.includes(key)));
    const placedTypes = new Set(columns.flat());

    visibleTypes
      .filter((key) => !placedTypes.has(key))
      .forEach((key, index) => {
        columns[index % columns.length].push(key);
      });

    return columns;
  };

  const cardColumns = buildCardColumns();
  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)";
  const sectionColor = tokens.accent;

  const renderToggleButton = (configType: string, configCode: string, val: string) => {
    const isSelected = selectedRules[configKey(configType, configCode)]?.value === val;
    const sel = getSelectedStyle(val);
    const valueLabel = getLabel("productConfigLabels", val);

    return (
      <Box key={val} sx={{ display: "inline-block", width: "auto" }}>
        <FieldFocusPopup
          enabled
          content={getConfigTooltip(configType, configCode, val)}
          placement="bottom-start"
        >
          <Box
            component="button"
            onClick={() => handleSelect(configType, configCode, val)}
            sx={{
              minHeight: 34,
              display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.75, borderRadius: "999px",
              border: `1px solid ${isSelected ? sel.border : cardBorder}`,
              background: isSelected ? sel.bg : (isDark ? "rgba(255,255,255,0.025)" : "#FFFFFF"),
              color: isSelected ? sel.text : (isDark ? tokens.textSecondary : "rgba(15,23,42,0.82)"),
              fontFamily: "inherit",
              fontWeight: isSelected ? 700 : 600, fontSize: "0.78rem", letterSpacing: "0.01em",
              boxShadow: isSelected ? `0 0 0 1px ${sel.border} inset` : "none",
              cursor: canEdit ? "pointer" : "default", transition: "all 0.18s ease", outline: "none",
              "&:hover": canEdit ? {
                borderColor: sel.border,
                color: sel.text,
                background: sel.bg,
              } : {},
            }}
          >
            {isSelected && "\u2713 "}
            {valueLabel}
          </Box>
        </FieldFocusPopup>
      </Box>
    );
  };

  const renderEnableSwitchSection = (configType: string, configCode: string, allowedValues: string[]) => {
    const isAutoSignature = configType === AUTO_SIGNATURE_TYPE;
    // CS-2090 - Auto signature shows as on when no rule is stored yet, matching the backend default.
    // const currentValue = selectedRules[configKey(configType, configCode)]?.value;
    const currentValue = isAutoSignature
      ? autoSignatureValue
      : selectedRules[configKey(configType, configCode)]?.value;
    const isEnabled = currentValue === YES_VALUE;
    const yesValue = allowedValues.includes(YES_VALUE) ? YES_VALUE : allowedValues[0];
    const noValue = allowedValues.includes(NO_VALUE) ? NO_VALUE : allowedValues[1] || allowedValues[0];
    const meta = CONFIG_TYPE_META[configType] || CONFIG_TYPE_META.PURCHASE_AND_BILL;
    const isPurchaseAndBill = configType === PURCHASE_AND_BILL_TYPE;
    const isUpfrontPricing = configType === UPFRONT_PRICING_TYPE;
    const isLateFee = configType === LATE_FEE_TYPE;
    const discountValue =
      selectedRules[configKey(UPFRONT_PRICING_TYPE, ENABLE_CODE)]?.metadata?.[UPFRONT_DISCOUNT_METADATA_KEY] ?? 0;
    // CS-1764 - Late fee percentage from metadata
    const lateFeePercentValue =
      selectedRules[configKey(LATE_FEE_TYPE, ENABLE_CODE)]?.metadata?.[LATE_FEE_PERCENT_METADATA_KEY] ?? 0;

    return (
      <Box>
        <FieldFocusPopup
          enabled
          content={getConfigTooltip(configType, configCode, isEnabled ? yesValue : noValue)}
          placement="bottom-start"
        >
          <Box
            sx={{
              mt: 1.2,
              p: 1.6,
              borderRadius: "8px",
              border: `1px solid ${cardBorder}`,
              background: isDark ? "rgba(2,6,23,0.18)" : "rgba(248,250,252,0.74)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box sx={{ color: meta.color, display: "flex", flexShrink: 0 }}>
                {/* CS-1764 - Late fee uses AccessTime icon for clock/late visual */}
                {isUpfrontPricing ? <BoltOutlined sx={{ fontSize: 16 }} /> : isLateFee ? <AccessTime sx={{ fontSize: 16 }} /> : isAutoSignature ? <DrawOutlined sx={{ fontSize: 16 }} /> : <AttachMoneyOutlined sx={{ fontSize: 16 }} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.25 }}>
                {isPurchaseAndBill ? "Enable Purchase & Bill" : isLateFee ? "Enable Late Fee" : isAutoSignature ? "Enable Auto Signature" : "Enable Upfront Pricing"}
              </Typography>
              <Typography sx={{ fontSize: "0.74rem", color: tokens.textSecondary, lineHeight: 1.25, mt: 0.25 }}>
                {isPurchaseAndBill ? "Unlocks upfront pricing configuration" : isLateFee ? "Adds a Late Fee % column to agreements" : isAutoSignature ? "Automatically apply a countersignature after the practice signs." : "Discounted upfront payment option"}
              </Typography>
              </Box>
            </Box>
            <Switch
              checked={isEnabled}
              disabled={!canEdit}
              onChange={(event) => handleSelect(configType, configCode, event.target.checked ? yesValue : noValue)}
              sx={{
                width: 48,
                height: 28,
                p: 0,
                flexShrink: 0,
                "& .MuiSwitch-switchBase": {
                  p: 0.45,
                  "&.Mui-checked": {
                    transform: "translateX(20px)",
                    color: "#FFFFFF",
                    "& + .MuiSwitch-track": {
                      backgroundColor: meta.color,
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 20,
                  height: 20,
                  boxShadow: "0 2px 8px rgba(15,23,42,0.26)",
                },
                "& .MuiSwitch-track": {
                  borderRadius: 999,
                  backgroundColor: isDark ? "rgba(15,23,42,0.95)" : "rgba(148,163,184,0.35)",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </FieldFocusPopup>

        {isUpfrontPricing && isEnabled && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: tokens.textSecondary, mb: 0.8 }}>
              Upfront Discount %
            </Typography>
            <Box
              sx={{
                height: 38,
                px: 1.4,
                borderRadius: "7px",
                border: `1px solid ${isDark ? "rgba(15,23,42,0.95)" : "rgba(148,163,184,0.3)"}`,
                background: isDark ? "rgba(2,6,23,0.78)" : "#FFFFFF",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                component="input"
                type="number"
                min={0}
                max={100}
                step={1}
                value={String(discountValue)}
                disabled={!canEdit}
                onChange={(event) => handleUpfrontDiscountChange(event.target.value)}
                onBlur={(event) => {
                  if (event.target.value === "") handleUpfrontDiscountChange("0");
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: tokens.textPrimary,
                  fontSize: "0.86rem",
                  fontWeight: 700,
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  MozAppearance: "textfield",
                }}
              />
              <Typography sx={{ fontSize: "0.84rem", color: tokens.textSecondary }}>%</Typography>
            </Box>
            <Typography sx={{ fontSize: "0.74rem", color: tokens.textSecondary, lineHeight: 1.35, mt: 1.1 }}>
              Applied to Extended Price during agreement creation.
            </Typography>
          </Box>
        )}

        {/* CS-1764 - Late Fee percentage input when enabled */}
        {isLateFee && isEnabled && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: tokens.textSecondary, mb: 0.8 }}>
              Late Fee %
            </Typography>
            <Box
              sx={{
                height: 38,
                px: 1.4,
                borderRadius: "7px",
                border: `1px solid ${isDark ? "rgba(15,23,42,0.95)" : "rgba(148,163,184,0.3)"}`,
                background: isDark ? "rgba(2,6,23,0.78)" : "#FFFFFF",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                component="input"
                type="number"
                min={0}
                max={100}
                step={1}
                value={String(lateFeePercentValue)}
                disabled={!canEdit}
                onChange={(event) => handleLateFeePercentChange(event.target.value)}
                onBlur={(event) => {
                  if (event.target.value === "") handleLateFeePercentChange("0");
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: tokens.textPrimary,
                  fontSize: "0.86rem",
                  fontWeight: 700,
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
              <Typography sx={{ fontSize: "0.84rem", color: tokens.textSecondary }}>%</Typography>
            </Box>
            <Typography sx={{ fontSize: "0.74rem", color: tokens.textSecondary, lineHeight: 1.35, mt: 1.1 }}>
              Applied to overdue balances on agreements.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderCodeSection = (configType: string, configCode: string, allowedValues: string[]) => (
    // CS-2090 - Keyed by type + code because the Purchase & Bill card now renders two ENABLE codes
    // (its own and the auto signature one), and a bare configCode key would collide.
    // Previous version, kept for reference: <Box key={configCode} mb={2}>
    <Box key={configKey(configType, configCode)} mb={2}>
      {/* CS-1764 - Added LATE_FEE_TYPE to enable switch rendering */}
      {/* CS-2090 - Added AUTO_SIGNATURE_TYPE to enable switch rendering. Previous version, kept for reference:
          {[PURCHASE_AND_BILL_TYPE, UPFRONT_PRICING_TYPE, LATE_FEE_TYPE].includes(configType) ? ( */}
      {[PURCHASE_AND_BILL_TYPE, UPFRONT_PRICING_TYPE, LATE_FEE_TYPE, AUTO_SIGNATURE_TYPE].includes(configType) ? (
        renderEnableSwitchSection(configType, configCode, allowedValues)
      ) : (
        <>
          {configType !== PURCHASE_AND_BILL_TYPE && (
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: tokens.textSecondary, mb: 0.8 }}>
              {getLabel("productConfigLabels", configCode)}
            </Typography>
          )}
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {allowedValues.map((val) => renderToggleButton(configType, configCode, val))}
          </Box>
        </>
      )}
    </Box>
  );

  const renderCardHeader = (configType: string) => {
    const meta = CONFIG_TYPE_META[configType] || {
      label: configType,
      description: "",
      icon: <SettingsOutlined sx={{ fontSize: 18 }} />,
      color: tokens.accent,
    };
    return (
      <Box
        mb={2}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.4,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "11px",
            backgroundColor: `${meta.color}1a`,
            border: `1px solid ${meta.color}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: meta.color,
          }}
        >
          {meta.icon}
        </Box>
        <Box sx={{ minWidth: 0, pt: 0.2 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.94rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
              letterSpacing: "-0.005em",
            }}
          >
            {getLabel("productConfigLabels", configType, meta.label)}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.76rem",
              color: tokens.textSecondary,
              lineHeight: 1.45,
              mt: 0.3,
            }}
          >
            {meta.description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const configContent = (
    <>
      {loading ? (
        <Box sx={{ px: 2.8, py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box sx={{ mx: embedded ? 0 : 2.8, mt: embedded ? 0 : 2, p: 1.5, borderRadius: "14px", border: `1px solid ${tokens.border}`, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", backgroundColor: tokens.cardBg }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                py: 0.5,
                borderRadius: "999px",
                border: "1px solid rgba(34,197,94,0.35)",
                backgroundColor: "rgba(34,197,94,0.12)",
              }}
            >
              <FiberManualRecord sx={{ fontSize: 8, color: "#22C55E" }} />
              <Typography sx={{ fontWeight: 800, color: "#22C55E", fontSize: "0.78rem", letterSpacing: "0.02em" }}>
                ACTIVE
              </Typography>
            </Box>
            {activeEntries.map(([key, rule]) => {
              const [type, code] = key.split(".");
              const typeMeta = CONFIG_TYPE_META[type];
              // CS-2090 - Added AUTO_SIGNATURE_TYPE so the chip reads "Auto Signature", not "Enable".
              // Previous version, kept for reference: the same expression without the
              // `type === AUTO_SIGNATURE_TYPE` clause below.
              const codeLabel =
                type === PURCHASE_AND_BILL_TYPE ||
                type === UPFRONT_PRICING_TYPE ||
                type === LATE_FEE_TYPE ||
                type === AUTO_SIGNATURE_TYPE
                  ? getLabel("productConfigLabels", type, typeMeta?.label ?? type)
                  : getLabel("productConfigLabels", code);
              const valueLabel = getLabel("productConfigLabels", rule.value);
              const dotColor = getDotColor(rule.value);

              return (
                <Box key={key} sx={{ display: "inline-block", width: "auto" }}>
                  <FieldFocusPopup
                    enabled
                    content={getConfigTooltip(type, code, rule.value)}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.6,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "999px",
                        border: `1px solid ${dotColor}40`,
                        backgroundColor: `${dotColor}14`,
                      }}
                    >
                      <FiberManualRecord sx={{ fontSize: 8, color: dotColor }} />
                      <Typography sx={{ fontWeight: 700, color: tokens.textPrimary, fontSize: "0.78rem", mr: 0.3 }}>{codeLabel}</Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary }}>=</Typography>
                      <Typography sx={{ ml: 0.3, fontSize: "0.78rem", fontWeight: 700, color: dotColor }}>{valueLabel}</Typography>
                    </Box>
                  </FieldFocusPopup>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ px: embedded ? 0 : 2.8, pt: 2.5, pb: 0.75 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "10px",
                  backgroundColor: `${sectionColor}1a`,
                  border: `1px solid ${sectionColor}33`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: `0 4px 10px ${sectionColor}26`,
                }}
              >
                <BoltOutlined sx={{ color: sectionColor, fontSize: 17 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.74rem",
                  fontWeight: 800,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: sectionColor,
                }}
              >
                Product Rules
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: sectionColor, opacity: 0.2, ml: 0.5 }} />
            </Box>
          </Box>

          <Box
            sx={{
              px: embedded ? 0 : 2.8,
              pb: embedded ? 0 : 2,
              pt: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 2,
              alignItems: "stretch",
            }}
          >
            {cardColumns.map((column, columnIndex) => (
              <Box key={columnIndex} sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
                {column.map((type) => {
                  const cardAccent = (CONFIG_TYPE_META[type] || CONFIG_TYPE_META.PURCHASE_AND_BILL).color;
                  return (
                    <Box
                      key={type}
                      sx={{
                        p: 2.25,
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                        borderRadius: "14px",
                        boxShadow: isDark ? "0 6px 18px rgba(0,0,0,0.22)" : "0 2px 8px rgba(15,23,42,0.05)",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                        flex: 1,
                        "&:hover": {
                          borderColor: `${cardAccent}45`,
                          boxShadow: isDark ? "0 10px 24px rgba(0,0,0,0.3)" : "0 8px 20px rgba(15,23,42,0.08)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {renderCardHeader(type)}
                      {Object.entries(template[type] || {}).map(([c, v]) => renderCodeSection(type, c, v))}
                      {/* CS-2090 - Auto signature toggle lives inside the Purchase & Bill card, directly
                          below the Purchase & Bill toggle, with no card header of its own. */}
                      {type === PURCHASE_AND_BILL_TYPE
                        && isSectionVisible(AUTO_SIGNATURE_TYPE)
                        && autoSignatureAllowedValues
                        && renderCodeSection(AUTO_SIGNATURE_TYPE, ENABLE_CODE, autoSignatureAllowedValues)}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </>
      )}
    </>
  );

  const configFooter = canEdit && (
    <Box sx={{ ...footerSx, ...(embedded ? { px: 0, border: "none", pt: 2 } : {}) }}>
      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
        
        <Button
          type="button"
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={saving || loading || (!isDraftMode && productId <= 0)}
          startIcon={saving ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <SaveOutlined />}
          sx={primaryBtnSx}
        >
          {isDraftMode ? "Save Config" : "Save"}
        </Button>
        {!embedded && (
          <Button type="button" variant="outlined" disableElevation onClick={onClose} startIcon={<Cancel />} sx={cancelBtnSx}>
            Close
          </Button>
        )}
        <Button
          color="error"
          variant="outlined"
          size="small"
          startIcon={<CancelOutlined sx={{ fontSize: "1rem !important" }} />}
          onClick={() => navigate("/products/explore_market")}
          sx={{
            ...cancelBtnSx,
            fontSize: "0.78rem",
            py: 0.5,
            px: 1.5,
            minWidth: "auto",
            borderRadius: "8px",
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  if (embedded) {
    if (!open) return null;

    return (
      <Box
        sx={{
          borderRadius: "12px",
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.bg,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <TuneOutlined sx={{ fontSize: 20, color: tokens.accent }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: tokens.textPrimary, letterSpacing: "-0.01em", lineHeight: 1.3 }}>
              Product Configuration
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.25 }}>
              {productName ? `${productName} · Configure product rules and settings` : "Configure product rules and settings"}
            </Typography>
          </Box>
        </Box>
        {configContent}
        {configFooter}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{ elevation: 0, sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <TuneOutlined sx={{ fontSize: 20, color: tokens.accent }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: tokens.textPrimary, lineHeight: 1.3 }}>
            Product Configuration
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            {productName ? `${productName} • Configure product rules and settings` : "Configure product rules and settings"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <Close sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={{ ...bodySx, gap: 0, p: 0 }}>
        {configContent}
      </Box>

      {configFooter}
    </Dialog>
  );
};

export default ProductConfigPopup;